import forOwn from 'lodash/forOwn'
import type { FormikConfig, FormikProps } from 'formik'
import { Formik, Form as FormikForm } from 'formik'
import * as React from 'react'
import { useError } from '@ztechapps/common.hook'

export enum FormStatus {
  initial = 'initial',
  success = 'success',
  error = 'error',
}

export type FormProps = FormikConfig<any> & {
  afterSubmit?: (values: any, formikBag: FormikProps<any>) => void
  afterReset?: (values: any, formikBag: FormikProps<any>) => void
}

export const Form = ({ children, onSubmit, onReset, afterSubmit, afterReset, ...formikProps }: FormProps) => {
  const { onError } = useError()

  const onSubmitForm = async (values, formikBag) => {
    try {
      formikBag.setStatus(FormStatus.initial)
      if (onSubmit) {
        const result = await onSubmit?.(values, formikBag)

        //throw if there are some errors
        const errors = result?.errors

        if (errors) throw errors
      }

      ///submission was done so set the formik status
      formikBag.setStatus(FormStatus.success)

      //set the after submit
      afterReset && afterSubmit(values, formikBag)
    } catch (e) {
      //set the error status
      formikBag.setStatus(FormStatus.error)

      let errorMessage = e
      //get the error message here, and we are just interested with the first item of the message
      const validationError = e?.validationError
      if (validationError) {
        errorMessage = 'Please fix the validation errors on the form.'
        //if there is an error set the error for that fields
        //loop thru all error fields
        forOwn(validationError, (value, key) => {
          formikBag?.setFieldError(key, value)
        })
      }
      onError(errorMessage)
    }
  }

  const onResetForm = async (values, formikBag) => {
    if (onReset) {
      await onReset(values, formikBag)
    }

    afterReset && afterReset(values, formikBag)
  }

  const useFormikContext = typeof children !== 'function'

  if (useFormikContext) {
    return (
      <Formik onSubmit={onSubmitForm} {...formikProps}>
        <FormikForm noValidate={true}>{children}</FormikForm>
      </Formik>
    )
  }

  const childFunc = children as unknown as (props: FormikProps<any>) => any

  return (
    <Formik onSubmit={onSubmitForm} onReset={onResetForm} {...formikProps}>
      {(formikProps) => <FormikForm noValidate={true}>{childFunc(formikProps)}</FormikForm>}
    </Formik>
  )
}
